import React from 'react';
import { Package, Ruler, Lock, CloudRain, Truck } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';

const StorageUnitsSection = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const specifications = [
    { icon: Ruler, label: 'Length', value: '5.90m (19.4 ft)' },
    { icon: Ruler, label: 'Width', value: '2.35m (7.7 ft)' },
    { icon: Ruler, label: 'Height', value: '2.39m (7.8 ft)' },
    { icon: Package, label: 'Volume', value: '33m³ (1,165 ft³)' },
  ];

  const features = [
    { icon: Package, title: 'New Containers', desc: 'Brand new "one-trip" 20ft shipping containers' },
    { icon: Lock, title: 'Secure Lock Box', desc: 'Each container fitted with lock box protection' },
    { icon: CloudRain, title: 'Weatherproof', desc: 'Complete protection from the elements' },
    { icon: Truck, title: 'Concreted Yard', desc: 'Fully accessible by car, van or lorry' },
  ];

  return (
    <section id="storage" className="py-16 md:py-24 bg-gradient-to-br from-gray-50 to-red-50/20">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <span className="inline-flex items-center px-4 py-2 rounded-full bg-red-100 text-red-700 text-sm font-medium mb-4">
            <Package className="h-4 w-4 mr-2" />
            Our Storage Units
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            20ft Shipping Container Storage
          </h2>
          <p className="text-lg text-gray-600">
            High-quality, brand new containers offering secure and weatherproof storage for all your needs.
          </p>
        </div>

        {/* Main Container Showcase */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          {/* Image */}
          <div className="relative">
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://customer-assets.emergentagent.com/job_red-sails-storage/artifacts/aeyucykm_20241220_113513.jpg"
                alt="20ft Storage Container"
                className="w-full h-auto object-cover"
                loading="lazy"
                decoding="async"
                style={{ maxWidth: '100%', height: 'auto', display: 'block' }}
              />
            </div>
            {/* Floating Badge */}
            <div className="absolute -bottom-4 -right-4 bg-white rounded-xl shadow-xl p-6">
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-1">Container Size</p>
                <p className="text-3xl font-bold text-red-600">20ft</p>
                <p className="text-sm text-gray-600">Standard</p>
              </div>
            </div>
          </div>

          {/* Specifications */}
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-gray-900">Container Specifications</h3>
            <p className="text-gray-600">
              Internal dimensions of our standard 20ft shipping containers provide ample space 
              for furniture, equipment, inventory, or personal belongings.
            </p>

            <div className="grid sm:grid-cols-2 gap-4">
              {specifications.map((spec, index) => {
                const Icon = spec.icon;
                return (
                  <Card key={index} className="border-2 hover:border-red-200 transition-colors">
                    <CardContent className="p-6">
                      <div className="flex items-center space-x-3 mb-2">
                        <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                          <Icon className="h-5 w-5 text-red-600" />
                        </div>
                        <p className="font-semibold text-gray-900">{spec.label}</p>
                      </div>
                      <p className="text-2xl font-bold text-red-600 ml-13">{spec.value}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            <div className="bg-red-50 border-l-4 border-red-600 p-6 rounded-lg">
              <p className="font-semibold text-gray-900 mb-2">What's Included:</p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-red-600 rounded-full mr-3" />
                  Heavy-duty industry standard padlock
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-red-600 rounded-full mr-3" />
                  Secure lock box fitted to container
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-red-600 rounded-full mr-3" />
                  Easy-access opening handles
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-red-600 rounded-full mr-3" />
                  Completely weatherproof construction
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-red-600 rounded-full mr-3" />
                  Fully concreted yard with vehicle access
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <Card
                key={index}
                className="border-2 hover:border-red-200 hover:shadow-lg transition-all duration-300 group"
              >
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4 group-hover:bg-red-600 transition-colors duration-300">
                    <Icon className="h-8 w-8 text-red-600 group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-sm text-gray-600">{feature.desc}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* CTA */}
        <div className="text-center">
          <Button
            onClick={() => scrollToSection('contact')}
            size="lg"
            className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
          >
            Reserve Your Container Today
          </Button>
        </div>
      </div>
    </section>
  );
};

export default StorageUnitsSection;
